import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';
import { enableMapSet } from 'immer';
import type {
  AbilityScores,
  AbilityScoreMethod,
  AbilityName,
  Alignment,
  SkillName,
} from '@character-creator/types';
import { getRulesetConfig } from '@character-creator/data';
import { sanitizeCharacterName } from '../utils/formatting';


// Import types from separate files
import type {
  WizardStep,
  DockTab,
  IdentitySubTab,
  EquipmentSubTab,
  SectionStatus,
  AsiChoice,
  ClassLevel,
  CharacterDraft,
} from './types';

// Import constants from separate files
import { WIZARD_STEPS } from './constants';

// Re-export types and constants for backwards compatibility
export type { WizardStep, DockTab, IdentitySubTab, EquipmentSubTab, SectionStatus, AsiChoice, ClassLevel, CharacterDraft };
export { WIZARD_STEPS, STEP_INFO, DOCK_TABS, IDENTITY_SUB_TABS, EQUIPMENT_SUB_TABS, DOCK_TAB_INFO } from './constants';

// Enable Immer's MapSet plugin for Set support in completedSteps
enableMapSet();

const DEFAULT_ABILITY_SCORES: AbilityScores = {
  strength: 8,
  dexterity: 8,
  constitution: 8,
  intelligence: 8,
  wisdom: 8,
  charisma: 8,
};

/**
 * Create a new character draft with fresh metadata (ID and timestamps)
 */
function createNewDraft(rulesetId: string = 'srd-5.1'): CharacterDraft {
  const now = new Date().toISOString();
  return {
    // Metadata
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    playerName: undefined,
    campaignName: undefined,

    // Ruleset
    rulesetId,

    // Basics
    name: '',
    characterLevel: 1,

    // Race
    raceId: null,
    subraceId: null,
    abilityBonusChoices: [],
    languageChoices: [],

    // Class
    classLevels: [],
    classId: null,
    skillProficiencyChoices: [],
    confirmedCommoner: false,

    // Abilities
    abilityScoreMethod: 'point-buy',
    abilityScores: { ...DEFAULT_ABILITY_SCORES },

    // Background
    backgroundId: null,
    confirmedNoBackground: false,

    // Equipment
    equipmentSelections: {},
    weaponChoiceResolutions: {},
    customEquipment: [],
    currency: { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 },

    // Attacks
    attacks: [],

    // Spells
    selectedCantrips: [],
    knownSpells: [],
    preparedSpells: [],
    selectedFamiliarId: null,

    // Details
    alignment: 'true-neutral',
    personalityTraits: [],
    ideals: [],
    bonds: [],
    flaws: [],
    backstory: '',
    appearance: {},
  };
}

// Initial draft with fresh metadata
const INITIAL_DRAFT: CharacterDraft = createNewDraft();

/**
 * Helper to find a class level by classId in the draft
 */
function findClassLevel(draft: CharacterDraft, classId: string): ClassLevel | undefined {
  return draft.classLevels.find((cl) => cl.classId === classId);
}

/**
 * Helper to toggle an item in an array (add if not present, remove if present)
 */
function toggleArrayItem<T>(array: T[], item: T): void {
  const index = array.indexOf(item);
  if (index === -1) {
    array.push(item);
  } else {
    array.splice(index, 1);
  }
}

interface CharacterCreationState {
  currentStep: WizardStep;
  draft: CharacterDraft;
  completedSteps: Set<WizardStep>;
  isPreviewOpen: boolean;

  // New dock navigation state
  currentDockTab: DockTab;
  identitySubTab: IdentitySubTab;
  equipmentSubTab: EquipmentSubTab;
  isMenuOpen: boolean;

  // Legacy navigation (now free-form, can jump to any section)
  setStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  canNavigateTo: (step: WizardStep) => boolean;
  getSectionStatus: (section: WizardStep) => SectionStatus;

  // Dock navigation
  setDockTab: (tab: DockTab) => void;
  setIdentitySubTab: (subTab: IdentitySubTab) => void;
  setEquipmentSubTab: (subTab: EquipmentSubTab) => void;
  getDockTabStatus: (tab: DockTab) => SectionStatus;
  toggleMenu: () => void;

  // Preview panel
  togglePreview: () => void;

  // Basics
  setName: (name: string) => void;

  // Race
  setRace: (raceId: string | null) => void;
  setSubrace: (subraceId: string | null) => void;
  setAbilityBonusChoices: (choices: { ability: AbilityName; bonus: number }[]) => void;
  setLanguageChoices: (languages: string[]) => void;

  // Class (multiclass support)
  setClass: (classId: string | null) => void; // Sets primary class (legacy)
  setSkillProficiencyChoices: (skills: SkillName[]) => void; // For primary class
  setConfirmedCommoner: (confirmed: boolean) => void;
  setCharacterLevel: (level: number) => void;
  addClassLevel: (classId: string, skills: SkillName[]) => void;
  removeClassLevel: (classId: string) => void;
  setClassLevelSkills: (classId: string, skills: SkillName[]) => void;
  setSubclass: (classId: string, subclassId: string | null) => void;
  setFightingStyle: (classId: string, fightingStyleId: string | null) => void;
  toggleExpertise: (classId: string, skill: SkillName) => void;
  setLandCircleTerrain: (classId: string, terrainId: string | null) => void;
  setPactBoon: (classId: string, pactBoonId: string | null) => void;
  toggleFavoredEnemy: (classId: string, enemyId: string) => void;
  toggleNaturalExplorerTerrain: (classId: string, terrainId: string) => void;
  toggleMetamagic: (classId: string, metamagicId: string) => void;
  toggleEldritchInvocation: (classId: string, invocationId: string) => void;
  setAsiChoice: (classId: string, asiLevel: number, choice: AsiChoice) => void;
  clearAsiChoice: (classId: string, asiLevel: number) => void;
  incrementClassLevel: (classId: string) => void;
  decrementClassLevel: (classId: string) => void;

  // Abilities
  setAbilityScoreMethod: (method: AbilityScoreMethod) => void;
  setAbilityScore: (ability: AbilityName, value: number) => void;
  setAbilityScores: (scores: AbilityScores) => void;
  resetAbilityScores: () => void;

  // Background
  setBackground: (backgroundId: string | null) => void;
  setConfirmedNoBackground: (confirmed: boolean) => void;

  // Equipment
  setEquipmentChoices: (selections: Record<number, number>) => void;
  setWeaponChoiceResolution: (key: string, weaponId: string) => void;
  setCustomEquipment: (items: string[]) => void;
  addCustomEquipmentItem: (item: string) => void;
  removeCustomEquipmentItem: (index: number) => void;
  setCurrency: (currency: Partial<CharacterDraft['currency']>) => void;

  // Attacks
  setAttacks: (attacks: CharacterDraft['attacks']) => void;
  addAttack: (attack: CharacterDraft['attacks'][0]) => void;
  updateAttack: (index: number, attack: Partial<CharacterDraft['attacks'][0]>) => void;
  removeAttack: (index: number) => void;

  // Spells
  selectCantrip: (spellId: string) => void;
  deselectCantrip: (spellId: string) => void;
  setSelectedCantrips: (spellIds: string[]) => void;
  selectKnownSpell: (spellId: string) => void;
  deselectKnownSpell: (spellId: string) => void;
  setKnownSpells: (spellIds: string[]) => void;
  selectPreparedSpell: (spellId: string) => void;
  deselectPreparedSpell: (spellId: string) => void;
  setPreparedSpells: (spellIds: string[]) => void;
  clearSpells: () => void;
  setFamiliar: (familiarId: string | null) => void;

  // Details
  setAlignment: (alignment: Alignment) => void;
  setPersonalityTraits: (traits: string[]) => void;
  setIdeals: (ideals: string[]) => void;
  setBonds: (bonds: string[]) => void;
  setFlaws: (flaws: string[]) => void;
  setBackstory: (backstory: string) => void;
  setAppearance: (appearance: Partial<CharacterDraft['appearance']>) => void;

  // Metadata
  setPlayerName: (name: string) => void;
  setCampaignName: (name: string) => void;

  // Step completion
  markStepComplete: (step: WizardStep) => void;
  isStepComplete: (step: WizardStep) => boolean;

  // Clear sections
  clearBasics: () => void;
  clearRace: () => void;
  clearClass: () => void;
  clearBackground: () => void;

  // Ruleset
  setRulesetId: (rulesetId: string) => void;

  // Reset
  reset: () => void;
}

export const useCharacterCreationStore = create<CharacterCreationState>()(
  persist(
    immer((set, get) => ({
    currentStep: 'basics',
    draft: { ...INITIAL_DRAFT },
    completedSteps: new Set<WizardStep>(),
    isPreviewOpen: true,

    // New dock navigation state
    currentDockTab: 'identity',
    identitySubTab: 'basics',
    equipmentSubTab: 'inventory',
    isMenuOpen: false,

    // Navigation (free-form - can jump to any section)
    setStep: (step) =>
      set((state) => {
        if (get().canNavigateTo(step)) {
          state.currentStep = step;
        }
      }),

    nextStep: () =>
      set((state) => {
        const currentIndex = WIZARD_STEPS.indexOf(state.currentStep);
        if (currentIndex < WIZARD_STEPS.length - 1) {
          state.currentStep = WIZARD_STEPS[currentIndex + 1];
        }
      }),

    prevStep: () =>
      set((state) => {
        const currentIndex = WIZARD_STEPS.indexOf(state.currentStep);
        if (currentIndex > 0) {
          state.currentStep = WIZARD_STEPS[currentIndex - 1];
        }
      }),

    canNavigateTo: () => true, // Free navigation - can jump to any section

    getSectionStatus: (section) => {
      const state = get();
      const draft = state.draft;

      // Check if this is the current section (in-progress)
      if (state.currentStep === section) return 'in-progress';

      // Check completion based on section requirements
      switch (section) {
        case 'basics':
          return draft.name.trim().length > 0 ? 'complete' : 'incomplete';
        case 'race':
          return draft.raceId !== null ? 'complete' : 'incomplete';
        case 'class':
          return draft.classId !== null || draft.confirmedCommoner
            ? 'complete'
            : 'incomplete';
        case 'abilities':
          // Complete if any score is different from default (user made choices)
          return Object.values(draft.abilityScores).some((score) => score !== 8)
            ? 'complete'
            : 'incomplete';
        case 'background':
          return draft.backgroundId !== null ? 'complete' : 'incomplete';
        case 'details':
          // Details are optional, mark complete if any detail is filled
          return draft.backstory.trim().length > 0 ||
            draft.personalityTraits.length > 0
            ? 'complete'
            : 'incomplete';
        case 'equipment':
          // Equipment is auto-populated, consider complete if class/background chosen
          return draft.classId !== null || draft.backgroundId !== null
            ? 'complete'
            : 'incomplete';
        case 'combat':
          // Combat is optional, always considered complete
          return 'complete';
        case 'spells':
          // Spells is optional, always considered complete
          return 'complete';
        case 'review':
          return 'incomplete'; // Review is always the final step
        default:
          return 'incomplete';
      }
    },

    // Preview panel
    togglePreview: () =>
      set((state) => {
        state.isPreviewOpen = !state.isPreviewOpen;
      }),

    // Dock navigation
    setDockTab: (tab) =>
      set((state) => {
        state.currentDockTab = tab;
      }),

    setIdentitySubTab: (subTab) =>
      set((state) => {
        state.identitySubTab = subTab;
      }),

    setEquipmentSubTab: (subTab) =>
      set((state) => {
        state.equipmentSubTab = subTab;
      }),

    getDockTabStatus: (tab) => {
      const state = get();
      const draft = state.draft;

      switch (tab) {
        case 'identity': {
          // Identity is complete if name, race, class, abilities, and background are set
          const hasName = draft.name.trim().length > 0;
          const hasRace = draft.raceId !== null;
          const hasClass = draft.classId !== null || draft.confirmedCommoner;
          const hasAbilities = Object.values(draft.abilityScores).some((score) => score !== 8);
          const hasBackground = draft.backgroundId !== null;
          if (hasName && hasRace && hasClass && hasAbilities && hasBackground) return 'complete';
          if (hasName || hasRace || hasClass || hasAbilities || hasBackground) return 'in-progress';
          return 'incomplete';
        }

        case 'equipment':
          // Equipment has auto-populated defaults, complete if class/background chosen
          return draft.classId !== null || draft.backgroundId !== null
            ? 'complete'
            : 'incomplete';

        case 'spells':
          // Spells is optional - always considered complete
          return 'complete';

        default:
          return 'incomplete';
      }
    },

    toggleMenu: () =>
      set((state) => {
        state.isMenuOpen = !state.isMenuOpen;
      }),

    // Basics
    setName: (name) =>
      set((state) => {
        // Sanitize input to prevent XSS and limit to safe characters
        state.draft.name = sanitizeCharacterName(name);
      }),

    // Race
    setRace: (raceId) =>
      set((state) => {
        state.draft.raceId = raceId;
        state.draft.subraceId = null;
        state.draft.abilityBonusChoices = [];
        state.draft.languageChoices = [];
      }),

    setSubrace: (subraceId) =>
      set((state) => {
        state.draft.subraceId = subraceId;
      }),

    setAbilityBonusChoices: (choices) =>
      set((state) => {
        state.draft.abilityBonusChoices = choices;
      }),

    setLanguageChoices: (languages) =>
      set((state) => {
        state.draft.languageChoices = languages;
      }),

    // Class (with multiclass support)
    setClass: (classId) =>
      set((state) => {
        state.draft.classId = classId;
        state.draft.skillProficiencyChoices = [];
        state.draft.confirmedCommoner = false;
        state.draft.weaponChoiceResolutions = {};
        // Update classLevels to match
        if (classId) {
          state.draft.classLevels = [{ classId, level: 1, skillProficiencyChoices: [], subclassId: null, fightingStyleId: null, expertiseSkills: [], landCircleTerrainId: null, pactBoonId: null, favoredEnemies: [], naturalExplorerTerrains: [], metamagicChoices: [], eldritchInvocations: [], asiChoices: {} }];
        } else {
          state.draft.classLevels = [];
        }
      }),

    setSkillProficiencyChoices: (skills) =>
      set((state) => {
        state.draft.skillProficiencyChoices = skills;
        // Sync with classLevels
        if (state.draft.classLevels.length > 0) {
          state.draft.classLevels[0].skillProficiencyChoices = skills;
        }
      }),

    setConfirmedCommoner: (confirmed) =>
      set((state) => {
        state.draft.confirmedCommoner = confirmed;
        if (confirmed) {
          state.draft.classId = null;
          state.draft.skillProficiencyChoices = [];
          state.draft.classLevels = [];
        }
      }),

    setCharacterLevel: (level) =>
      set((state) => {
        state.draft.characterLevel = Math.max(1, Math.min(20, level));
      }),

    addClassLevel: (classId, skills) =>
      set((state) => {
        const existing = findClassLevel(state.draft, classId);
        if (existing) {
          existing.level += 1;
        } else {
          state.draft.classLevels.push({
            classId,
            level: 1,
            skillProficiencyChoices: skills,
            subclassId: null,
            fightingStyleId: null,
            expertiseSkills: [],
            landCircleTerrainId: null,
            pactBoonId: null,
            favoredEnemies: [],
            naturalExplorerTerrains: [],
            metamagicChoices: [],
            eldritchInvocations: [],
            asiChoices: {},
          });
        }
        // Recalculate total level
        state.draft.characterLevel = state.draft.classLevels.reduce(
          (sum, cl) => sum + cl.level,
          0
        );
        // Update legacy helpers if this is primary class
        if (state.draft.classLevels.length === 1) {
          state.draft.classId = classId;
          state.draft.skillProficiencyChoices = skills;
        }
      }),

    removeClassLevel: (classId) =>
      set((state) => {
        state.draft.classLevels = state.draft.classLevels.filter(
          (cl) => cl.classId !== classId
        );
        // Recalculate total level
        state.draft.characterLevel = Math.max(
          1,
          state.draft.classLevels.reduce((sum, cl) => sum + cl.level, 0)
        );
        // Update legacy helpers
        if (state.draft.classLevels.length > 0) {
          state.draft.classId = state.draft.classLevels[0].classId;
          state.draft.skillProficiencyChoices =
            state.draft.classLevels[0].skillProficiencyChoices;
        } else {
          state.draft.classId = null;
          state.draft.skillProficiencyChoices = [];
        }
      }),

    setClassLevelSkills: (classId, skills) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          classLevel.skillProficiencyChoices = skills;
          // Sync legacy if primary class
          if (state.draft.classLevels[0]?.classId === classId) {
            state.draft.skillProficiencyChoices = skills;
          }
        }
      }),

    setSubclass: (classId, subclassId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          classLevel.subclassId = subclassId;
        }
      }),

    setFightingStyle: (classId, fightingStyleId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          classLevel.fightingStyleId = fightingStyleId;
        }
      }),

    toggleExpertise: (classId, skill) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          toggleArrayItem(classLevel.expertiseSkills, skill);
        }
      }),

    setLandCircleTerrain: (classId, terrainId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          classLevel.landCircleTerrainId = terrainId;
        }
      }),

    setPactBoon: (classId, pactBoonId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          classLevel.pactBoonId = pactBoonId;
        }
      }),

    toggleFavoredEnemy: (classId, enemyId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          toggleArrayItem(classLevel.favoredEnemies, enemyId);
        }
      }),

    toggleNaturalExplorerTerrain: (classId, terrainId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          toggleArrayItem(classLevel.naturalExplorerTerrains, terrainId);
        }
      }),

    toggleMetamagic: (classId, metamagicId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          toggleArrayItem(classLevel.metamagicChoices, metamagicId);
        }
      }),

    toggleEldritchInvocation: (classId, invocationId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          toggleArrayItem(classLevel.eldritchInvocations, invocationId);
        }
      }),

    setAsiChoice: (classId, asiLevel, choice) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          classLevel.asiChoices[asiLevel] = choice;
        }
      }),

    clearAsiChoice: (classId, asiLevel) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel) {
          delete classLevel.asiChoices[asiLevel];
        }
      }),

    incrementClassLevel: (classId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel && state.draft.characterLevel < 20) {
          classLevel.level += 1;
          state.draft.characterLevel += 1;
        }
      }),

    decrementClassLevel: (classId) =>
      set((state) => {
        const classLevel = findClassLevel(state.draft, classId);
        if (classLevel && classLevel.level > 1) {
          classLevel.level -= 1;
          state.draft.characterLevel -= 1;
        } else if (classLevel && classLevel.level === 1) {
          // Remove the class entirely
          state.draft.classLevels = state.draft.classLevels.filter(
            (cl) => cl.classId !== classId
          );
          state.draft.characterLevel = Math.max(
            1,
            state.draft.classLevels.reduce((sum, cl) => sum + cl.level, 0)
          );
          // Update legacy helpers
          if (state.draft.classLevels.length > 0) {
            state.draft.classId = state.draft.classLevels[0].classId;
            state.draft.skillProficiencyChoices =
              state.draft.classLevels[0].skillProficiencyChoices;
          } else {
            state.draft.classId = null;
            state.draft.skillProficiencyChoices = [];
          }
        }
      }),

    // Abilities
    setAbilityScoreMethod: (method) =>
      set((state) => {
        state.draft.abilityScoreMethod = method;
        // Reset scores when changing method
        state.draft.abilityScores = { ...DEFAULT_ABILITY_SCORES };
      }),

    setAbilityScore: (ability, value) =>
      set((state) => {
        state.draft.abilityScores[ability] = value;
      }),

    setAbilityScores: (scores) =>
      set((state) => {
        state.draft.abilityScores = scores;
      }),

    resetAbilityScores: () =>
      set((state) => {
        state.draft.abilityScores = { ...DEFAULT_ABILITY_SCORES };
      }),

    // Background
    setBackground: (backgroundId) =>
      set((state) => {
        state.draft.backgroundId = backgroundId;
        if (backgroundId !== null) {
          state.draft.confirmedNoBackground = false;
        }
      }),

    setConfirmedNoBackground: (confirmed) =>
      set((state) => {
        state.draft.confirmedNoBackground = confirmed;
        if (confirmed) {
          state.draft.backgroundId = null;
        }
      }),

    // Equipment
    setEquipmentChoices: (selections) =>
      set((state) => {
        state.draft.equipmentSelections = selections;
      }),

    setWeaponChoiceResolution: (key, weaponId) =>
      set((state) => {
        state.draft.weaponChoiceResolutions[key] = weaponId;
      }),

    setCustomEquipment: (items) =>
      set((state) => {
        state.draft.customEquipment = items;
      }),

    addCustomEquipmentItem: (item) =>
      set((state) => {
        if (item.trim()) {
          state.draft.customEquipment.push(item.trim());
        }
      }),

    removeCustomEquipmentItem: (index) =>
      set((state) => {
        state.draft.customEquipment.splice(index, 1);
      }),

    setCurrency: (currency) =>
      set((state) => {
        state.draft.currency = { ...state.draft.currency, ...currency };
      }),

    // Attacks
    setAttacks: (attacks) =>
      set((state) => {
        state.draft.attacks = attacks;
      }),

    addAttack: (attack) =>
      set((state) => {
        if (!state.draft.attacks) {
          state.draft.attacks = [];
        }
        state.draft.attacks.push(attack);
      }),

    updateAttack: (index, attack) =>
      set((state) => {
        if (state.draft.attacks?.[index]) {
          state.draft.attacks[index] = { ...state.draft.attacks[index], ...attack };
        }
      }),

    removeAttack: (index) =>
      set((state) => {
        if (state.draft.attacks) {
          state.draft.attacks.splice(index, 1);
        }
      }),

    // Spells
    selectCantrip: (spellId) =>
      set((state) => {
        if (!state.draft.selectedCantrips.includes(spellId)) {
          state.draft.selectedCantrips.push(spellId);
        }
      }),

    deselectCantrip: (spellId) =>
      set((state) => {
        state.draft.selectedCantrips = state.draft.selectedCantrips.filter(
          (id) => id !== spellId
        );
      }),

    setSelectedCantrips: (spellIds) =>
      set((state) => {
        state.draft.selectedCantrips = [...spellIds];
      }),

    selectKnownSpell: (spellId) =>
      set((state) => {
        if (!state.draft.knownSpells.includes(spellId)) {
          state.draft.knownSpells.push(spellId);
        }
      }),

    deselectKnownSpell: (spellId) =>
      set((state) => {
        state.draft.knownSpells = state.draft.knownSpells.filter(
          (id) => id !== spellId
        );
      }),

    setKnownSpells: (spellIds) =>
      set((state) => {
        state.draft.knownSpells = [...spellIds];
      }),

    selectPreparedSpell: (spellId) =>
      set((state) => {
        if (!state.draft.preparedSpells.includes(spellId)) {
          state.draft.preparedSpells.push(spellId);
        }
      }),

    deselectPreparedSpell: (spellId) =>
      set((state) => {
        state.draft.preparedSpells = state.draft.preparedSpells.filter(
          (id) => id !== spellId
        );
      }),

    setPreparedSpells: (spellIds) =>
      set((state) => {
        state.draft.preparedSpells = [...spellIds];
      }),

    clearSpells: () =>
      set((state) => {
        state.draft.selectedCantrips = [];
        state.draft.knownSpells = [];
        state.draft.preparedSpells = [];
      }),

    setFamiliar: (familiarId) =>
      set((state) => {
        state.draft.selectedFamiliarId = familiarId;
      }),

    // Details
    setAlignment: (alignment) =>
      set((state) => {
        state.draft.alignment = alignment;
      }),

    setPersonalityTraits: (traits) =>
      set((state) => {
        state.draft.personalityTraits = traits;
      }),

    setIdeals: (ideals) =>
      set((state) => {
        state.draft.ideals = ideals;
      }),

    setBonds: (bonds) =>
      set((state) => {
        state.draft.bonds = bonds;
      }),

    setFlaws: (flaws) =>
      set((state) => {
        state.draft.flaws = flaws;
      }),

    setBackstory: (backstory) =>
      set((state) => {
        state.draft.backstory = backstory;
      }),

    setAppearance: (appearance) =>
      set((state) => {
        state.draft.appearance = { ...state.draft.appearance, ...appearance };
      }),

    // Metadata
    setPlayerName: (name) =>
      set((state) => {
        state.draft.playerName = name || undefined;
        state.draft.updatedAt = new Date().toISOString();
      }),

    setCampaignName: (name) =>
      set((state) => {
        state.draft.campaignName = name || undefined;
        state.draft.updatedAt = new Date().toISOString();
      }),

    // Step completion
    markStepComplete: (step) =>
      set((state) => {
        state.completedSteps.add(step);
      }),

    isStepComplete: (step) => get().completedSteps.has(step),

    // Clear sections
    clearBasics: () =>
      set((state) => {
        state.draft.name = '';
        state.draft.alignment = 'true-neutral';
        state.draft.personalityTraits = [];
        state.draft.ideals = [];
        state.draft.bonds = [];
        state.draft.flaws = [];
      }),

    clearRace: () =>
      set((state) => {
        state.draft.raceId = null;
        state.draft.subraceId = null;
        state.draft.abilityBonusChoices = [];
        state.draft.languageChoices = [];
        state.draft.appearance = {};
      }),

    clearClass: () =>
      set((state) => {
        state.draft.classId = null;
        state.draft.classLevels = [];
        state.draft.skillProficiencyChoices = [];
        state.draft.confirmedCommoner = false;
        state.draft.characterLevel = 1;
        state.draft.weaponChoiceResolutions = {};
      }),

    clearBackground: () =>
      set((state) => {
        state.draft.backgroundId = null;
        state.draft.confirmedNoBackground = false;
        state.draft.backstory = '';
      }),

    // Ruleset
    setRulesetId: (rulesetId) =>
      set((state) => {
        state.draft.rulesetId = rulesetId;
        // Set starting level from ruleset config
        const config = getRulesetConfig(rulesetId);
        if (config?.startingLevel) {
          state.draft.characterLevel = config.startingLevel;
        }
      }),

    // Reset
    reset: () =>
      set((state) => {
        state.currentStep = 'basics';
        // Create fresh draft with new ID and timestamps
        state.draft = createNewDraft(state.draft.rulesetId);
        state.completedSteps = new Set();
        state.isPreviewOpen = true;
        // Reset dock navigation
        state.currentDockTab = 'identity';
        state.identitySubTab = 'basics';
        state.equipmentSubTab = 'inventory';
        state.isMenuOpen = false;
      }),
  })),
  {
    name: 'character-draft',
    storage: createJSONStorage(() => localStorage),
    partialize: (state) => ({
      currentStep: state.currentStep,
      draft: state.draft,
      // Convert Set to Array for JSON serialization
      completedSteps: Array.from(state.completedSteps),
      // Persist dock navigation state
      currentDockTab: state.currentDockTab,
      identitySubTab: state.identitySubTab,
      equipmentSubTab: state.equipmentSubTab,
    }),
    // Custom merge to handle Set conversion and dock tab migration
    // Note: characterLevel is now computed from classLevels in consuming components
    // to avoid a Zustand v5 + Immer middleware persist desync issue
    merge: (persistedState, currentState) => {
      const persisted = persistedState as Partial<CharacterCreationState> & Record<string, unknown>;

      // Migrate old 'gear'/'combat' dock tabs to 'equipment'
      const rawDockTab = persisted.currentDockTab as string | undefined;
      const currentDockTab: DockTab =
        rawDockTab === 'gear' || rawDockTab === 'combat'
          ? 'equipment'
          : (persisted.currentDockTab ?? currentState.currentDockTab);

      return {
        ...currentState,
        ...persisted,
        currentDockTab,
        // Convert completedSteps array back to Set
        completedSteps: new Set(persisted.completedSteps as unknown as WizardStep[]),
      };
    },
  }
));
